// src/storage/essayProgress.js
//
// Pure, framework-free helpers for the student "Your Progress" before/after view
// (essay-progress-beforeafter slice). Derives per-prompt first-mark -> latest-mark
// progress ENTIRELY from the existing owner-read /users/{uid}/essayAttempts list --
// no new reads, no schema/rules/functions change. JSX-free so it unit-tests under
// `node --test`; MyEssaysPanel just renders the result.
//
// Grades are parsed from the persisted aiFeedback. Both live vocabularies are a
// "Grade N" string (marks mode stamps gradeLabel(n)='Grade '+n; band-only mode uses
// the AI_GRADE_BANDS vocab, live = "Grade 1".."Grade 9"); OLD records may hold the
// legacy "Level N" vocab -- both parse. Unparseable feedback contributes nothing
// (never a fabricated number, never a throw).

// ---- grade parsing ----

// "Grade 7" / "Level 4" (tolerant of surrounding whitespace + case) -> the ordinal
// and a clean display label in the record's OWN vocabulary. Anything else -> null.
function parseBandLabel(s) {
    if (typeof s !== 'string') return null;
    const m = /^(grade|level)\s+(\d+)$/i.exec(s.trim());
    if (!m) return null;
    const n = parseInt(m[2], 10);
    if (!Number.isInteger(n) || n < 1 || n > 9) return null;
    const vocab = m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase();
    return { ordinal: n, label: vocab + ' ' + n };
}

// { ordinal, label } for a stored aiFeedback map, or null. Prefers mostLikelyBand;
// else the (rounded) midpoint of indicativeBandRange low..high; a half-parseable
// range uses the side that parses.
export function gradeInfo(aiFeedback) {
    const fb = aiFeedback || {};
    if (fb.status === 'draft') return null;
    const most = parseBandLabel(fb.mostLikelyBand);
    if (most) return most;
    const range = fb.indicativeBandRange || {};
    const lo = parseBandLabel(range.low);
    const hi = parseBandLabel(range.high);
    if (lo && hi) {
        const mid = Math.round((lo.ordinal + hi.ordinal) / 2);
        return { ordinal: mid, label: lo.label.split(' ')[0] + ' ' + mid };
    }
    return lo || hi || null;
}

// Spec surface: the bare integer 1-9, or null when the record has no parseable grade.
export function gradeOrdinal(aiFeedback) {
    const info = gradeInfo(aiFeedback);
    return info ? info.ordinal : null;
}

// ---- grouping ----

// Same tolerant timestamp read as essayAttemptHelpers (Firestore Timestamp /
// {seconds} / Date / ms number) -- EXCEPT that a missing takenAt sorts LAST, not
// first. Rules force takenAt == request.time on every persisted attempt, so the
// only null-takenAt record a client ever sees is the latency-compensation snapshot
// of a locally PENDING serverTimestamp() write -- i.e. the NEWEST attempt. Sorting
// it first would transiently invert first/latest (a fresh redraft gain would flash
// as a drop at the exact moment the user earned it).
function takenAtMillis(a) {
    const t = a && a.takenAt;
    if (!t) return Infinity;
    if (typeof t.toMillis === 'function') return t.toMillis();
    if (typeof t.seconds === 'number') return t.seconds * 1000;
    if (t instanceof Date) return t.getTime();
    const n = Number(t);
    return Number.isFinite(n) ? n : Infinity;
}

// Own-essay questions pair up as redrafts of the SAME pasted question only when the
// text genuinely matches: trim + lowercase + collapse whitespace.
function normalizeOwnPrompt(t) {
    return String(t == null ? '' : t).trim().toLowerCase().replace(/\s+/g, ' ');
}

// Group attempts by the prompt they answer. Key: pack prompts -> packId + promptId;
// own essays (source === 'own') -> packId + normalized promptText (different own
// questions stay separate). Groups are ordered by their earliest attempt; each
// group's attempts are sorted oldest -> newest.
export function groupAttemptsByPrompt(attempts) {
    const oldestFirst = (Array.isArray(attempts) ? attempts.slice() : [])
        .sort((a, b) => takenAtMillis(a) - takenAtMillis(b));
    const byKey = new Map();
    for (const a of oldestFirst) {
        if (!a || !a.packId) continue;
        const own = a.source === 'own';
        const idPart = own ? normalizeOwnPrompt(a.promptText) : String(a.promptId == null ? '' : a.promptId);
        if (!idPart) continue; // no identity to group on
        const key = String(a.packId) + '\n' + idPart;
        let g = byKey.get(key);
        if (!g) {
            const title = (a.promptTitle && String(a.promptTitle).trim())
                || (own ? normalizeOwnPrompt(a.promptText).slice(0, 80) : String(a.promptId))
                || 'Practice essay';
            g = { key, packId: a.packId, title, source: own ? 'own' : 'pack', attempts: [] };
            byKey.set(key, g);
        }
        g.attempts.push(a);
    }
    return Array.from(byKey.values());
}

// ---- per-group progress ----

// Before/after for one group, using the EARLIEST and LATEST attempts that carry a
// parseable grade. Fewer than 2 gradeable attempts -> null (no progress to show;
// ungradeable records simply don't contribute).
export function progressForGroup(group) {
    const atts = (group && Array.isArray(group.attempts)) ? group.attempts : [];
    const gradeable = [];
    for (const a of atts) {
        const info = gradeInfo(a && a.aiFeedback);
        if (info) gradeable.push({ attempt: a, info });
    }
    if (gradeable.length < 2) return null;
    const f = gradeable[0];
    const l = gradeable[gradeable.length - 1];
    return {
        first: { ordinal: f.info.ordinal, band: f.info.label, takenAt: f.attempt.takenAt },
        latest: { ordinal: l.info.ordinal, band: l.info.label, takenAt: l.attempt.takenAt },
        delta: l.info.ordinal - f.info.ordinal,
        attemptCount: atts.length,
        // lesson-3c: EVERY gradeable mark in order, so the row can draw the shape of
        // the journey rather than just its endpoints. Additive -- first/latest/delta
        // are unchanged, so nothing that already reads this is affected.
        series: gradeable.map((g) => g.info.ordinal),
    };
}

// ---- sparkline ----

// Ordinals -> points inside a `width` x `height` box, for the tiny SVG line in a
// progress row. Pure (no DOM), so the mapping is testable rather than eyeballed.
//
// The vertical scale spans the SERIES' own min..max, not the full 1-9 grade scale:
// across two or three drafts a student moves a grade or two, and stretching that over
// the whole scale would flatten every real journey into a horizontal line. `pad`
// keeps the end dot's radius inside the box.
//
// A flat series (every mark identical) has no range to scale over, so it is drawn on
// the vertical CENTRE -- a straight line through the middle, which is the honest
// picture of "steady" and never a divide-by-zero.
export function sparklineGeometry(series, width = 64, height = 20, pad = 3) {
    const xs = (Array.isArray(series) ? series : []).filter((n) => Number.isFinite(n));
    if (xs.length === 0) return { points: [], d: '' };
    const w = Math.max(1, Number(width) || 0);
    const h = Math.max(1, Number(height) || 0);
    const p = Math.max(0, Math.min(Number(pad) || 0, Math.floor(h / 2)));
    const min = Math.min(...xs);
    const max = Math.max(...xs);
    const span = max - min;
    const innerH = Math.max(0, h - p * 2);
    const points = xs.map((v, i) => {
        const x = xs.length === 1 ? w / 2 : (i / (xs.length - 1)) * w;
        // SVG y grows downward, so a HIGHER grade must sit HIGHER on screen.
        const y = span === 0 ? h / 2 : p + (1 - (v - min) / span) * innerH;
        return { x: round2(x), y: round2(y) };
    });
    return { points, d: points.map((pt, i) => (i === 0 ? 'M' : 'L') + pt.x + ' ' + pt.y).join(' ') };
}

function round2(n) {
    return Math.round(n * 100) / 100;
}

// ---- headline summary ----

// Everything the "Your Progress" card renders. groupsWithProgress rows are sorted
// biggest gain first (delta desc, then most recent latest attempt); bestGain is the
// top IMPROVED group (null when nothing improved -- flat/down groups still list,
// rendered neutrally by the panel).
export function summarizeProgress(attempts) {
    const withProgress = [];
    for (const g of groupAttemptsByPrompt(attempts)) {
        const progress = progressForGroup(g);
        if (progress) withProgress.push({ ...g, progress });
    }
    withProgress.sort((a, b) =>
        (b.progress.delta - a.progress.delta)
        || (takenAtMillis({ takenAt: b.progress.latest.takenAt }) - takenAtMillis({ takenAt: a.progress.latest.takenAt })));
    const improved = withProgress.filter((g) => g.progress.delta > 0);
    const top = improved[0] || null; // sort order makes [0] the best gain
    return {
        redraftedCount: withProgress.length,
        improvedCount: improved.length,
        bestGain: top ? {
            title: top.title,
            from: top.progress.first.band,
            to: top.progress.latest.band,
            delta: top.progress.delta,
        } : null,
        groupsWithProgress: withProgress,
    };
}
